// =========================================================
// Vojjin STORAGE Utilities
// =========================================================
import {registerGlobal} from "./core.js";

/* global Logger */
/* global Hash */
/* global Net */

class Vault {
    static #_storages = {
        local: window.localStorage,
        session: window.sessionStorage
    };

    // Default storage type
    static #_active = "local";
    // Internal helper to get correct storage
    static #S = () => this.#_storages[this.#_active] ?? window.localStorage;
    static #CACHE_NAME = "gemini-storage-cache";

    // Switch between storage types
    /**
     * Sets the active storage layer to the specified storage type.
     *
     * The function checks if the provided `where` parameter corresponds to an
     * existing storage layer. If the specified storage does not exist and is
     * not set to "cache," it defaults to the "local" storage layer.
     *
     * @param {string} [where="local"] - The name of the storage layer to activate.
     *                                   Acceptable values are "local", "cache",
     *                                   or any other defined storage type.
     * @returns {this} The current instance to allow for method chaining.
     */
    static use = (where = "local") => {
        if (!this.#_storages[where] && where !== "cache") where = "local";
        this.#_active = where;
        return this;
    };


    /**
     * Retrieves the value associated with the specified key.
     * The retrieval mechanism depends on the active storage type.
     * If the active storage type is set to "cache", it retrieves
     * the value from the cache. Otherwise, it retrieves the value
     * from the storage system configured via the private #S function.
     *
     * @param {string} key - The key whose associated value is to be retrieved.
     * @returns {*} The value associated with the given key.
     */
    static get = (key) => {
        if (this.#_active === "cache") return this.getCache(key);
        return this.#S().getItem(key);
    }

    /**
     * Sets a value for the specified key in the storage mechanism currently in use.
     *
     * Depending on the active storage type, the method either updates the cache mechanism
     * or the primary storage interface.
     *
     * - If the active storage type is "cache", the method delegates the operation to `setCache`.
     * - Otherwise, it stores the key-value pair in the primary storage.
     *
     * @param {string} key - The key for which the value needs to be set.
     * @param {string} value - The value to associate with the specified key. Defaults to an empty string if undefined.
     * @returns {this} The current instance to allow method chaining.
     */
    static set = (key, value) => {
        if (this.#_active === "cache") return this.setCache(key, value);
        this.#S().setItem(key, value ?? "");
        return this;
    };

    /**
     * Removes an item associated with the specified key from storage.
     * If the active storage type is "cache", the key is removed from the cache.
     * Otherwise, the key is removed from the underlying storage mechanism.
     *
     * @param {string} key - The key identifying the item to remove from storage.
     * @returns {Object} Returns the current instance for method chaining.
     */
    static remove = (key) => {
        if (this.#_active === "cache") return this.removeCache(key);
        const store = this.#S();
        if (!store.getItem(key)) return;
        store.removeItem(key);
        return this;
    }

    /**
     * Retrieves and parses a stored object based on the provided key.
     * If the active mode is "cache", the method fetches the object from the cache.
     * Otherwise, it retrieves the raw data using the provided key, attempts to parse it as JSON,
     * and returns the parsed object. If the data is unavailable or parsing fails, an empty object is returned.
     *
     * @param {string} key - The key used to retrieve the stored data.
     * @returns {object} The parsed object retrieved from the storage or cache, or an empty object if retrieval or parsing fails.
     */
    static getObject = (key) => {
        if (this.#_active === "cache") return this.getCacheJSON(key);
        const raw = this.#S().getItem(key);
        if (!raw) return {};
        try {
            return JSON.parse(raw);
        } catch {
            return {};
        }
    };

    /**
     * Stores a key-value pair in the appropriate storage medium based on the active mode.
     * If the active mode is "cache", the data will be stored using the `setCache` method.
     * Otherwise, the data will be stored in the default storage mechanism.
     * The value is serialized to a JSON string before storing.
     *
     * @param {string} key - The unique identifier for the data to be stored.
     * @param {*} value - The value to be associated with the key. It will be serialized to a JSON string.
     * @returns {this} The current instance to allow for method chaining.
     */
    static setObject = (key, value) => {
        if (this.#_active === "cache") return this.setCache(key, JSON.stringify(value));
        this.#S().setItem(key, JSON.stringify(value));
        return this;
    };

    /**
     * Retrieves the value of a specified key and id from an object, or returns a default value
     * if the key does not exist in the object.
     *
     * @function
     * @param {string} key - The key used to locate the object.
     * @param {string|number} id - The property key within the object to retrieve the value for.
     * @param {*} defaultValue - The value to return if the property is not found in the object.
     * @returns {*} The value associated with the specified id in the object, or the default value if not found.
     */
    static getObjectValue = (key, id, defaultValue) => {
        const obj = this.getObject(key);
        return Object.prototype.hasOwnProperty.call(obj, id)
            ? obj[id]
            : defaultValue;
    };


    /**
     * Retrieves the current active state.
     *
     * This method returns the state of the private `#_active` property,
     * which indicates the active status.
     *
     * @returns {*} The current value of the `#_active` property.
     */
    static active = () => this.#_active;

    // -----------------------------
    // Cache Storage (asynchronous)
    // -----------------------------

    /**
     * Retrieves a cached value for the specified key.
     *
     * This method attempts to retrieve a value from the Cache Storage API.
     * If Cache Storage is not supported in the current environment, it falls
     * back to using sessionStorage.
     *
     * @param {string} key - The key for the cached value to retrieve.
     * @returns {Promise<string|null>} A promise that resolves to the cached value
     * if found, or null if the key does not exist in the cache.
     * @throws {Error} If an error occurs while accessing the cache.
     */
    static getCache = async (key) => {
        if (typeof caches === "undefined") {
            Logger.warn("Cache Storage not supported, fallback to sessionStorage.");
            this.use("session");
            return this.get(key);
        }

        const cache = await caches.open(this.#CACHE_NAME);
        const res = await cache.match(key);
        if (!res) return null;
        return await res.text();
    };

    /**
     * Stores a key-value pair in the cache storage. If Cache Storage is not supported
     * by the environment, it falls back to using sessionStorage.
     *
     * The method first checks for the availability of Cache Storage. If it is available,
     * the key-value pair is stored using the Cache API. If unsupported, a warning is logged,
     * and the fallback storage mechanism is used instead.
     *
     * @async
     * @param {string} key - The unique identifier for the item to be cached.
     * @param {string} value - The value to be stored in the cache.
     * @returns {Promise<Object>} Returns the current context (`this`) for method chaining.
     */
    static setCache = async (key, value) => {
        if (typeof caches === "undefined") {
            Logger.warn("Cache Storage not supported, fallback to sessionStorage.");
            this.use("session");
            return this.set(key, value);
        }
        const cache = await caches.open(this.#CACHE_NAME);
        await cache.put(key, new Response(value));
        return this;
    };

    /**
     * Removes a cached resource identified by the provided key.
     *
     * This function attempts to delete a cached entry from the Cache Storage API.
     * If the Cache Storage API is unavailable (e.g., in unsupported browsers),
     * it falls back to using sessionStorage instead.
     *
     * @param {string} key - The key used to identify the cached resource.
     * @returns {Promise<Object>} A promise that resolves to the current instance
     * after the cache entry has been removed.
     */
    static removeCache = async (key) => {
        if (typeof caches === "undefined") {
            Logger.warn("Cache Storage not supported, fallback to sessionStorage.");
            this.use("session");
            return this.remove(key);
        }
        const cache = await caches.open(this.#CACHE_NAME);
        await cache.delete(key);
        return this;
    };

    /**
     * Retrieves a JSON object from the cache storage or session storage fallback.
     *
     * This asynchronous function attempts to retrieve a cached value for the provided key.
     * If the Cache Storage API is not supported, it falls back to using the session storage.
     * If the data is successfully retrieved and is a valid JSON string, it parses and returns
     * the corresponding object. Should parsing fail or no data exist for the given key,
     * an empty object is returned.
     *
     * @param {string} key - The key for which the cached JSON object is requested.
     * @returns {Promise<Object>} A promise that resolves to the JSON object retrieved from the cache, or an empty object if no data exists or parsing fails.
     */
    static getCacheJSON = async (key) => {
        if (typeof caches === "undefined") {
            Logger.warn("Cache Storage not supported, fallback to sessionStorage.");
            this.use("session");
            return this.getObject(key);
        }
        const text = await this.getCache(key);
        if (!text) return {};
        try {
            return JSON.parse(text);
        } catch {
            return {};
        }
    };

    /**
     * Fetches a resource while utilizing caching mechanisms for optimization.
     *
     * This function fetches data from a given URL and attempts to cache the response
     * for subsequent requests. It uses different caching strategies based on the
     * environment:
     *
     * - If the `Cache Storage` API is available (`caches` is defined):
     *   - Attempts to retrieve the cached response from the cache storage.
     *   - If unavailable, fetches the resource from the network and stores the
     *     response in the cache for future use.
     *   - Returns a `Response` object.
     *
     * - If the `Cache Storage` API is not available:
     *   - Falls back to using `sessionStorage` for caching. The response is keyed
     *     using the SHA-1 hash of the URL.
     *   - Attempts to retrieve the cached response from `sessionStorage`.
     *   - If unavailable, fetches the resource from the network and stores the
     *     response in `sessionStorage` for future use.
     *   - The response is parsed as JSON if possible; otherwise, the raw response
     *     is returned.
     *
     * Logs warnings and messages for scenarios such as unsupported caching APIs
     * or storage failures.
     *
     * @param {string} url - The URL of the resource to fetch.
     * @returns {Promise<any>} A promise that resolves to the fetched resource data.
     *                         For `Cache Storage`, a `Response` object is returned.
     *                         For `sessionStorage`, the parsed data or raw response
     *                         is returned.
     */
    static fetchWithCache = async (url) => {
        if (typeof caches === "undefined") {
            Logger.warn("Cache Storage not supported, fallback to sessionStorage.");

            let key = Hash.sha1(url);
            const cached = sessionStorage.getItem(key);
            if (cached) {
                Logger.log("Serving from sessionStorage cache:", url);
                try {
                    return JSON.parse(cached);
                } catch {
                    return cached;
                }
            }
            const response = await Net.fetch(url);
            let data;

            try {
                data = await response.json();
            } catch {
                data = response;
            }

            try {
                sessionStorage.setItem(key, JSON.stringify(data));
                Logger.log("Stored response in sessionStorage:", url);
            } catch (e) {
                Logger.warn("Failed to store in sessionStorage:", e.message);
            }

            return data;
        } else {

            this.use("cache");
            const cached = await this.get(url);
            if (cached) {
                return new Response(cached); // Recreate a proper Response object
            }
            // Otherwise fetch from network
            const response = await fetch(url);
            // Store the response text (clone, since fetch responses can be read only once)
            if (response.ok) {
                const text = await response.clone().text();
                await this.set(url, text);
            }
            return response;
        }
    }
}

registerGlobal('Vault', Vault);

